import { Component, ElementRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { MedicalHistoryQuestionsComponent } from '@components/consultation-request/medical-history-questions/medical-history-questions.component';
import { DynamicQuestionnaireFormComponent } from '@components/dynamic-forms/dynamic-questionnaire-form/dynamic-questionnaire-form.component';
import { ConsultationStatus } from '@enums/consultation-status';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { QuestionnaireAnswers } from '@enums/questionnaire-answers';
import { QuestionnaireStorageKeys } from '@enums/questionnaire-storage-keys';
import { UpsellSlugs } from '@enums/upsell-slugs';
import { environment } from '@environments/environment';
import { MissingFormFieldsError } from '@errors/missing-form-fields-error';
import { Address } from '@models/address';
import { ConsultationRequestOrderDetail } from '@models/consultation-request/consultation-request-order-detail';
import { DateData } from '@models/date-data';
import { DateSelection } from '@models/date-selection';
import { ExternalData } from '@models/dynamic-forms/external-data';
import { ShippingAddress } from '@models/shippingAddress';
import { Test } from '@models/test';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { DynamicFormsService } from '@services/dynamic-forms.service';
import { ErrorHandlerService } from '@services/error-handler.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { FormService } from '@services/form.service';
import { GenderService } from '@services/gender.service';
import { LoadingService } from '@services/loading.service';
import { NavigationService } from '@services/navigation.service';
import { OrderService } from '@services/order.service';
import { StorageService } from '@services/storage.service';
import { TreatmentTypeService } from '@services/treatment-type.service';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Component({
  template: '',
})
export abstract class ConsultationRequestFormComponent implements OnInit {
  @ViewChild('medicalQuestionsForm', { static: false })
  protected medicalQuestionsForm: DynamicQuestionnaireFormComponent;
  @ViewChild('medicalHistoryQuestionsForm', { static: false })
  protected medicalHistoryQuestionsForm: MedicalHistoryQuestionsComponent;
  @ViewChild('agreementQuestionsForm', { static: false })
  protected agreementQuestionsForm: DynamicQuestionnaireFormComponent;

  public QuestionnaireStorageKeys: typeof QuestionnaireStorageKeys = QuestionnaireStorageKeys;
  public responseError: string | null = null;
  public displayChooseAPharmacyComponent: boolean = false;
  public continueErrorMessage: string = null;
  public showLabFinderModal: boolean = false;
  public consultationRequestTreatmentType: ConsultationTreatmentTypes;
  public medicalQuestionIds: string[] = [];
  public isProcessing: boolean = false;
  public errorMessage: string | null = null;
  public submissionError: string | null = null;

  protected loadingService = inject(LoadingService);
  protected activatedRoute = inject(ActivatedRoute);
  protected consultationRequestService = inject(ConsultationRequestService);
  protected formService = inject(FormService);
  protected storageService = inject(StorageService);
  protected consultationStorageService = inject(ConsultationStorageService);
  protected title = inject(Title);
  protected dynamicFormsService = inject(DynamicFormsService);
  protected errorHandlerService = inject(ErrorHandlerService);
  protected navigationService = inject(NavigationService);
  protected genderService = inject(GenderService);
  protected errorLoggingService = inject(ErrorLoggingService);
  protected treatmentTypeService = inject(TreatmentTypeService);
  protected elementRef = inject(ElementRef);
  protected orderService = inject(OrderService);

  protected upsellIds: string[] = [];
  protected agreementQuestionIds: string[] = [];
  private orderShippingAddress: ShippingAddress;

  /**
   * Determines whether the bottom error message should be displayed.
   */
  public get shouldDisplayBottomError(): boolean {
    return (this.areFormsTouched() && !this.areFormsValid()) || !!this.responseError;
  }

  /**
   * Gets the treatment preferences
   */
  public get treatmentPreferences(): string {
    return this.treatmentPreferencesControl.value ?? this.consultationStorageService.treatmentPreferences;
  }

  /**
   * Gets the patient address form group.
   */
  public get addressForm(): FormGroup {
    return this.consultationRequestForm.get('address') as FormGroup;
  }

  /**
   * Gets the current consultation request.
   */
  public get consultationRequest(): any {
    return this.consultationRequestOrder?.consultationRequest;
  }

  /**
   * Gets the consultation request form group.
   */
  public get consultationRequestForm(): FormGroup {
    return this.formService.consultationRequestForm;
  }

  /**
   * Gets the treatment preferences form group.
   */
  public get treatmentPreferencesForm(): FormGroup {
    return this.formService.treatmentPreferencesForm;
  }

  /**
   * Gets whether the current consultation request is a UTI consultation request.
   */
  public get isUtiTreatmentType(): boolean {
    return this.consultationRequestTreatmentType === ConsultationTreatmentTypes.Uti;
  }

  /**
   * Gets the maximum age allowed for the consultation.
   */
  public get maxAge(): number {
    return environment.maxAgeAllowed;
  }

  /**
   * Gets the minimum age allowed for the consultation.
   */
  public get minAge(): number {
    return (
      this.treatmentTypeService.getTreatmentType(this.consultationRequestTreatmentType)?.minAgeAllowed ||
      environment.order.minAgeAllowed
    );
  }

  /**
   * Gets the questionnaire form.
   */
  public get questionnaireForm(): FormGroup {
    return this.medicalQuestionsForm.form;
  }

  /**
   * Gets the agreement questions form.
   */
  public get agreementForm(): FormGroup {
    return this.agreementQuestionsForm?.form;
  }

  /**
   * Gets the external validation data for the dynamic questionnaire form.
   */
  public get externalData(): ExternalData {
    return {
      age: this.patientAge,
      gender: this.patientInfoForm.value.gender,
      siteName: `${environment.site}.com`,
      treatmentName: this.consultationStorageService.getTreatmentPreferenceName(),
      treatmentType: this.consultationStorageService.treatmentType,
      pharmacyState: this.pharmacyForm.value.state,
      orderTests: this.consultationRequestOrder.tests.map(({ test_id }) => test_id),
      hypertensivePatient: this.hasYesAnswer(environment.consultation.highBloodPressureQuestionId),
      patientMedicatedForErectileDysfunction: this.hasYesAnswer(
        environment.consultation.erectileDysfunctionMedicationQuestionId,
      ),
      isLessThan28DaysSinceLastTreatment: this.isLessThan28DaysSinceLastTreatment(),
    };
  }

  /**
   * Gets the treatment preferences form control.
   */
  public get treatmentPreferencesControl(): FormControl {
    return this.treatmentPreferencesForm.get('treatmentPreferences') as FormControl;
  }

  /**
   * Gets the patient's age.
   */
  public get patientAge(): number {
    const { day, month, year } = this.patientInfoForm.get('birthday').value;

    return new DateSelection(+month, +day, +year).getAge();
  }

  /**
   * Gets the name of the indexed DB.
   */
  public get indexedDbName(): string {
    return `${this.consultationRequestOrder.transaction_id}-questionnaire`;
  }

  /**
   * Gets the current consultation request order.
   */
  public get consultationRequestOrder(): ConsultationRequestOrderDetail {
    return this.consultationRequestService.consultationOrderDetail;
  }

  /**
   * Gets the patient information form group.
   */
  public get patientInfoForm(): FormGroup {
    return this.consultationRequestForm.get('patient') as FormGroup;
  }

  /**
   * Gets the FormGroup for the pharmacy section.
   */
  public get pharmacyForm(): FormGroup {
    return this.consultationRequestForm.get('pharmacy') as FormGroup;
  }

  /**
   * Gets the medical history form.
   */
  public get medicalHistoryForm(): FormGroup {
    return this.medicalHistoryQuestionsForm?.form;
  }

  /**
   * Gets the title for the consultation request address component.
   */
  public get consultationRequestAddressTitle(): string {
    return this.isShippedOrder() ? 'Shipping Address' : 'Patient Address';
  }

  /**
   * This code listens for browser 'pageshow' events that occur when navigating using back/forward.
   *
   * If the page was loaded from browser cache (bfcache) the event.persisted is true, so this method updates
   * local storage with the current treatment type and reinitializes the component to ensure data consistency.
   *
   * bfcache docs: https://developer.mozilla.org/en-US/docs/Glossary/bfcache
   */
  @HostListener('window:pageshow', ['$event'])
  public onPageShow(event: PageTransitionEvent): void {
    if (event.persisted) {
      this.consultationStorageService.treatmentType = this.consultationRequestTreatmentType;
      this.ngOnInit();
    }
  }

  /**
   * Initializes the component by calling the specific initialization logic.
   */
  public ngOnInit(): void {
    this.initializeComponent();
    this.handleDesignatedPharmacy();
  }

  /**
   * Stores the consultation request data and navigates to the next page.
   */
  public continue(): void {
    this.responseError = null;
    this.submissionError = null;

    if (!this.canContinue()) {
      return;
    }

    this.isProcessing = true;
    this.handleSuccessWorkflow();
  }

  /**
   * Initializes the component.
   */
  protected initializeComponent(): void {
    this.title.setTitle(environment.titles.consultationRequest);
    this.handleConsultationOrderDetails();
    this.defineTreatmentPreferenceTests();
  }

  /**
   * Shows the lab finder modal.
   */
  protected presentLabFinderModal(): void {
    this.showLabFinderModal = true;
  }

  /**
   * Determines if the patient has disqualifying answers in the questionnaire.
   *
   * @returns {boolean} true if the patient has disqualifying answers, otherwise false
   */
  protected hasDisqualifyingAnswers(): boolean {
    return this.medicalQuestionsForm.questions.some(
      (question) =>
        this.dynamicFormsService.getQuestionVisibleWarnings(question, this.medicalQuestionsForm.form).length > 0,
    );
  }

  /**
   * Sets the questions and upsells ids for the current treatment type.
   *
   * @returns {Promise<void | string>} the question ids or an error message
   */
  protected setQuestions(): Promise<object | string> {
    return this.dynamicFormsService
      .getQuestionnaire(this.consultationRequestTreatmentType)
      .then((questionnaire) => {
        this.medicalQuestionIds = questionnaire?.medicalQuestionIds ?? [];
        this.upsellIds = questionnaire?.upsellIds ?? [];
        this.agreementQuestionIds = questionnaire?.agreementQuestionIds ?? [];

        return questionnaire;
      })
      .catch(() => (this.errorMessage = this.errorHandlerService.defaultErrorMessage));
  }

  /**
   * Handles the error of the consultation order details resolver.
   *
   * @param {string} errorMessage The error message
   */
  protected handleConsultationDetailsError(errorMessage: string): void {
    this.errorMessage = errorMessage;
    this.loadingService.toggleLoader(false);
  }

  /**
   * Set the consultation request order from the resolver and handles the error if any.
   */
  protected handleConsultationOrderDetails(): void {
    this.activatedRoute.data.subscribe(({ consultationOrderDetails }) => {
      if (consultationOrderDetails?.error) {
        this.handleConsultationDetailsError(consultationOrderDetails.errorMessage);

        return;
      }

      this.consultationRequestTreatmentType = consultationOrderDetails?.consultationRequest?.treatment_type;
      this.handleConsultationDetailsSuccess();
    });
  }

  /**
   * Handles the success of the consultation order details resolver.
   */
  protected handleConsultationDetailsSuccess(): void {
    this.addTreatmentPreferencesValidator();
    this.consultationStorageService.treatmentPreferences =
      this.consultationRequestService.getOrderedTreatmentPreferences();
    this.setQuestions().then(() => this.loadingService.toggleLoader(false));
  }

  /**
   * Show Choose a Pharmacy component when order is not shipped. Otherwise, set the pharmacy id to continue.
   */
  protected handlePharmacyForm(): void {
    if (this.isShippedOrder()) {
      this.patchConsultationPharmacyId();

      return;
    }

    this.displayChooseAPharmacyComponent = true;
  }

  /**
   * Patches the consultation pharmacy ID to the pharmacy form.
   */
  protected patchConsultationPharmacyId(): void {
    this.pharmacyForm.patchValue({ id: this.consultationRequest.pharmacy.id });
  }

  /**
   * Prefill the address form with the patient's shipping address if available
   */
  protected prefillShippingAddress(shippingAddress: ShippingAddress): void {
    this.addressForm.patchValue({
      streetAddress: shippingAddress.street,
      streetAddress2: shippingAddress?.street_2 ?? '',
      city: shippingAddress.city,
      state: shippingAddress.state.name_short,
      zipcode: shippingAddress.zip_code,
    });
  }

  /**
   * Sets the treatment preference tests based on the consultation request treatment type.
   *
   * Only specific treatment types (BacterialVaginosis, Metformin) are eligible for setting treatment preferences.
   * UTI orders have a separate component for treatment preferences and are excluded from this logic.
   */
  protected defineTreatmentPreferenceTests(): void {
    const treatmentTypeInstance = this.treatmentTypeService.getTreatmentType(this.consultationRequestTreatmentType);

    this.consultationStorageService.treatmentPreferenceTests = treatmentTypeInstance
      ? <Test[]>treatmentTypeInstance.getEligibleTreatmentTypes()
      : [];
  }

  /**
   * Handles the success workflow for the consultation request continue button.
   */
  protected handleSuccessWorkflow(): void {
    if (this.shouldUpdateShippingAddress()) {
      this.handleShippingAddressUpdate();
    }

    this.storeConsultationRequestCompletedData();
    this.isProcessing = false;
    this.navigationService.navigateToNextConsultationRequestPage(this.consultationStorageService.treatmentType);
  }

  protected handleDesignatedPharmacy() {
    if (!this.consultationRequestOrder) {
      return;
    }

    this.loadShippingAddress()
      .pipe(tap(() => this.handlePharmacyForm()))
      .subscribe();
  }

  /**
   * Marks all form controls as touched.
   */
  protected markAllFormControlsAsTouched(): void {
    this.patientInfoForm.markAllAsTouched();
    this.pharmacyForm.markAllAsTouched();
    this.treatmentPreferencesControl.markAllAsTouched();
    this.addressForm.markAllAsTouched();
    this.questionnaireForm.markAllAsTouched();
    this.medicalHistoryForm.markAllAsTouched();
    this.agreementForm?.markAllAsTouched();
  }

  /**
   * Sets the consultation status to disqualified if the patient is not eligible for the consultation.
   */
  protected getConsultationStatus(): ConsultationStatus {
    return this.genderService.getGenderInvalidError(
      this.consultationStorageService.treatmentType,
      this.consultationStorageService.patient.gender,
    ) !== null ||
      !this.isPatientAgeValid(this.consultationStorageService.patient.birthday) ||
      this.hasDisqualifyingAnswers()
      ? ConsultationStatus.Disqualified
      : ConsultationStatus.Pending;
  }

  /**
   * Checks if the forms displayed on the page are valid.
   *
   * @returns {boolean} true if all forms are valid, otherwise false
   */
  protected areFormsValid(): boolean {
    return (
      this.medicalHistoryForm?.valid &&
      !this.agreementForm?.invalid &&
      this.questionnaireForm?.valid &&
      this.patientInfoForm?.valid &&
      this.pharmacyForm?.valid &&
      this.treatmentPreferencesControl?.valid &&
      this.addressForm?.valid
    );
  }

  /**
   * Checks if the forms displayed on the page have been touched.
   */
  protected areFormsTouched(): boolean {
    return (
      this.medicalHistoryForm?.touched &&
      this.questionnaireForm?.touched &&
      this.patientInfoForm?.touched &&
      this.pharmacyForm?.touched &&
      this.treatmentPreferencesControl?.touched &&
      this.addressForm?.touched &&
      (this.agreementForm?.touched ?? true)
    );
  }

  /**
   * Stores the data that is needed for the consultation request completed page.
   */
  protected storeConsultationRequestCompletedData(): void {
    this.consultationStorageService.transactionId = this.consultationRequestOrder.transaction_id;
    this.consultationStorageService.consultationId = this.consultationRequestOrder.consultationRequest.id;
    this.consultationStorageService.hash = this.consultationRequestOrder.hash;
    this.consultationStorageService.patient = {
      name: this.patientInfoForm.get('first_name').value,
      last_name: this.patientInfoForm.get('last_name').value,
      email: this.consultationRequestOrder.email ?? this.patientInfoForm.get('email').value,
      phone: this.consultationRequestOrder.phone,
      birthday: this.patientInfoForm.get('birthday').value,
      gender: this.patientInfoForm.get('gender').value,
      height_feet: this.patientInfoForm.get('height_feet').value,
      height_inches: this.patientInfoForm.get('height_inches').value,
      weight: this.patientInfoForm.get('weight').value,
    };
    this.consultationStorageService.treatmentPreferences = this.treatmentPreferences;
    this.consultationStorageService.consultationStatus = this.getConsultationStatus();
    this.consultationStorageService.pharmacy = this.pharmacyForm.value;
    this.consultationStorageService.medicalHistoryQuestionnaireAnswers = this.medicalHistoryForm.value;

    this.consultationStorageService.address = this.addressForm.value;
    this.consultationStorageService.upsells = this.getUpsells();

    if (!this.consultationStorageService.upsells.includes(UpsellSlugs.DeliverMedication)) {
      this.consultationStorageService.setUpsellAddress(UpsellSlugs.DeliverMedication, null);
    }

    const isZofran = this.consultationStorageService.treatmentType === ConsultationTreatmentTypes.Zofran;
    const hasZofranUpsell =
      isZofran && this.consultationStorageService.upsells.includes(UpsellSlugs.ZofranTreatmentDissolvableTabletUpsell);

    if (hasZofranUpsell) {
      this.consultationStorageService.treatmentPreferences = UpsellSlugs.ZofranTreatmentDissolvableTabletUpsell;
    }

    if (!this.consultationStorageService.treatmentPreferences) {
      this.consultationStorageService.treatmentPreferences =
        this.consultationRequestService.getOrderedTreatmentPreferences();
    }
  }

  /**
   * Scrolls to the first invalid control in the page.
   */
  protected scrollToInvalidControl(): void {
    this.elementRef.nativeElement
      .querySelector('input.ng-invalid, textarea.ng-invalid, select.ng-invalid')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Returns true when the fullfillment method for the consultation request treatment type is delivery.
   */
  protected isShippedOrder() {
    const treatmentType = this.treatmentTypeService.getTreatmentType(this.consultationRequestTreatmentType);

    return treatmentType?.isShipped(this.consultationRequest);
  }

  /**
   * Calculates the difference in days between the current date and the provided date.
   *
   * @param {Date} date the date to compare with the current date
   *
   * @returns {number} the absolute difference in days between the current date and the provided date
   */
  private getDateDiffInDays(date: Date): number {
    const timeDiff = new Date().getTime() - date.getTime();

    return Math.abs(Math.floor(timeDiff / (1000 * 3600 * 24)));
  }

  /**
   * Sets the treatment preferences form control as required if the consultation request is a UTI consultation request.
   */
  private addTreatmentPreferencesValidator(): void {
    if (this.isUtiTreatmentType) {
      this.treatmentPreferencesControl?.addValidators(Validators.required);
      this.treatmentPreferencesControl?.updateValueAndValidity();
    }
  }

  /**
   * Returns true when the treatment is still valid. Otherwise, it will return false.
   *
   * This is a temporary method that will be removed when the cause of treatment type losing data in iOS is found.
   * We have created a custom stack tracker in global window method that will be reverted too.
   *
   * Meanwhile, if the treatment type is null or undefined, we will display a generic error message, so patient can
   * refresh the page and the questionnaire will be pre-filled with the selected options.
   */
  private isTreatmentTypeValid(treatmentType: ConsultationTreatmentTypes): boolean {
    if (!!treatmentType) {
      return true;
    }

    this.continueErrorMessage =
      'Unable to process your request. Please refresh the page. ' +
      `If you continue experiencing issues, contact ${environment.email} for assistance.`;

    this.errorLoggingService.logErrorWithStorage('Treatment type not found in local storage');

    return false;
  }

  /**
   * Checks if all forms are initialized.
   */
  private areAllFormInitialized(): boolean {
    if (
      this.medicalHistoryForm &&
      this.questionnaireForm &&
      this.patientInfoForm &&
      this.pharmacyForm &&
      this.treatmentPreferencesControl &&
      this.addressForm
    ) {
      return true;
    }

    this.continueErrorMessage =
      'Unable to process your request. Please refresh the page. ' +
      `If you continue experiencing issues, contact ${environment.email} for assistance.`;

    this.errorLoggingService.logErrorWithStorage('All forms are not initialized');

    return false;
  }

  /**
   * Determines if it has been less than 28 days since the last treatment was prescribed.
   *
   * @returns {boolean} true if it has been less than 28 days, otherwise false
   */
  private isLessThan28DaysSinceLastTreatment(): boolean {
    const lastPrescribedAt = this.consultationRequestOrder.last_prescribed_at;

    return lastPrescribedAt ? this.getDateDiffInDays(new Date(lastPrescribedAt)) < 28 : false;
  }

  /**
   * Determines if a patient's age makes them eligible for a consultation request.
   *
   * @param {DateData} patientBirthday the patient's date of birth
   *
   * @returns {boolean} true if the patient's age is within the valid range, otherwise false
   */
  private isPatientAgeValid(patientBirthday: DateData): boolean {
    const { day, month, year } = patientBirthday;

    return new DateSelection(+month, +day, +year).isAgeValid(this.minAge, this.maxAge);
  }

  /**
   * Gets the upsells selected by the patient.
   */
  private getUpsells(): string[] {
    const upsellAnswers = {
      ...this.consultationStorageService.getQuestionnaireAnswers(QuestionnaireStorageKeys.Upsells),
      ...this.consultationStorageService.upsellUpgrades,
    };
    if (!upsellAnswers) {
      return [];
    }
    const availableUpsells = Object.values(UpsellSlugs) as string[];

    return Object.values(upsellAnswers).filter((value: string) => availableUpsells.includes(value));
  }

  /**
   * Helper function to check if a questionnaire answer is "Yes".
   *
   * @param {string} questionId the ID of the question to check
   *
   * @returns {boolean} true if the answer is "Yes", false otherwise
   */
  private hasYesAnswer(questionId: string): boolean {
    return (
      this.consultationStorageService.getQuestionnaireAnswers(this.consultationStorageService.treatmentType)?.[
        questionId
      ] === QuestionnaireAnswers.Yes
    );
  }

  /**
   * Handles the shipping address update when the address form is dirty.
   */
  private loadShippingAddress(): Observable<void> {
    return this.orderService
      .getShippingAddresses(this.consultationRequestOrder.transaction_id, this.consultationRequestOrder.hash)
      .pipe(
        map((shippingAddresses) => {
          if (!shippingAddresses.data.length) {
            return;
          }

          this.orderShippingAddress = shippingAddresses.data[shippingAddresses.data.length - 1];
          this.consultationStorageService.shippingAddressId = this.orderShippingAddress.id;
          this.prefillShippingAddress(this.orderShippingAddress);
        }),
        catchError(() => {
          return of(void 0);
        }),
      );
  }

  /**
   * Determines whether the shipping address should be updated.
   */
  private shouldUpdateShippingAddress(): boolean {
    return this.orderShippingAddress && this.addressForm.dirty;
  }

  /**
   * Handles the shipping address update.
   */
  private handleShippingAddressUpdate(): void {
    const shippingAddress: Partial<ShippingAddress> = {
      street: this.addressForm.value.streetAddress,
      street_2: this.addressForm.value.streetAddress2,
      city: this.addressForm.value.city,
      state_id: Address.statesIds[this.addressForm.value.state],
      zip_code: this.addressForm.value.zipcode,
      id: this.orderShippingAddress.id,
    };

    this.orderService
      .updateShippingAddress(
        shippingAddress,
        this.consultationRequestOrder.transaction_id,
        this.consultationRequestOrder.hash,
      )
      .subscribe();
  }

  /**
   * Validates the forms before continuing.
   */
  private canContinue(): boolean {
    if (!this.areAllFormInitialized() || !this.isTreatmentTypeValid(this.consultationStorageService.treatmentType)) {
      return false;
    }

    if (!this.areFormsValid()) {
      this.markAllFormControlsAsTouched();
      this.scrollToInvalidControl();
      this.submissionError = new MissingFormFieldsError().message;

      return false;
    }

    return true;
  }
}
